import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

import CustomDropdown from "./CustomDropdown";
import ContractExpiryModal from "./ContractExpiryModal";
import RemovePlayerModal from "../club/RemovePlayerModal";
import ImageUploadModal from "../profile/ImageUploadModal";

export const DEFAULT_DETAILED_POSITIONS = {
  primary: "",
  secondary: "",
  tertiary: "",
};

export const DETAILED_POSITION_OPTIONS = [
  { label: "Goalkeeper", value: "gk" },
  { label: "Right Back", value: "rb" },
  { label: "Right Centre Back", value: "rcb" },
  { label: "Left Centre Back", value: "lcb" },
  { label: "Centre Back", value: "cb" },
  { label: "Left Back", value: "lb" },
  { label: "Right Wingback", value: "rwb" },
  { label: "Left Wingback", value: "lwb" },
  { label: "Right Back (5 at the back)", value: "rb5" },
  { label: "Left Back (5 at the back)", value: "lb5" },
  { label: "Right Centre Back (3 at the back)", value: "rcb3" },
  { label: "Left Centre Back (3 at the back)", value: "lcb3" },
  { label: "Centre Midfielder", value: "cm" },
  { label: "Right Centre Midfielder", value: "rcmf" },
  { label: "Left Centre Midfielder", value: "lcmf" },
  { label: "Right Centre Midfielder (3 at the back)", value: "rcmf3" },
  { label: "Left Centre Midfielder (3 at the back)", value: "lcmf3" },
  { label: "Defensive Midfielder", value: "dmf" },
  { label: "Right Defensive Midfielder", value: "rdmf" },
  { label: "Left Defensive Midfielder", value: "ldmf" },
  { label: "Attacking Midfielder", value: "amf" },
  { label: "Right Attacking Midfielder", value: "ramf" },
  { label: "Left Attacking Midfielder", value: "lamf" },
  { label: "Right Winger", value: "rw" },
  { label: "Left Winger", value: "lw" },
  { label: "Right Wing Forward", value: "rwf" },
  { label: "Left Wing Forward", value: "lwf" },
  { label: "Second Striker", value: "ss" },
  { label: "Striker", value: "cf" },
];

const PLAYER_IDENTIFIER_KEYS = [
  "id",
  "player_id",
  "account_id",
  "sportmonks_id",
  "uuid",
  "external_id",
  "slug",
  "email",
  "name",
];

const getPlayerIdentifier = (player) => {
  if (!player) return null;
  for (const key of PLAYER_IDENTIFIER_KEYS) {
    if (player[key]) {
      return player[key];
    }
  }
  return null;
};

const normalizePositionValue = (value) =>
  value ? value.toString().trim().toLowerCase() : "";

const buildFormattedPositions = (positions) => {
  const toUpper = (val) =>
    val && typeof val === "string" ? val.trim().toUpperCase() : null;

  return {
    primary_position: toUpper(positions.primary),
    secondary_position: toUpper(positions.secondary),
    teritory_position: toUpper(positions.tertiary),
  };
};

const arePositionsEqual = (
  a = DEFAULT_DETAILED_POSITIONS,
  b = DEFAULT_DETAILED_POSITIONS
) =>
  ["primary", "secondary", "tertiary"].every(
    (slot) =>
      normalizePositionValue(a?.[slot]) === normalizePositionValue(b?.[slot])
  );

export const extractDetailedPositionsFromPlayer = (player) => {
  const extracted = { ...DEFAULT_DETAILED_POSITIONS };

  if (!player) {
    return extracted;
  }

  const assignIfPresent = (slot, value) => {
    const normalized = normalizePositionValue(value);
    if (normalized) {
      extracted[slot] = normalized;
    }
  };

  const detailedObject =
    player?.tp_detailed_position ||
    player?.tpDetailedPosition ||
    player?.tpDetailedPositions ||
    player?.detailed_position_object;

  if (
    detailedObject &&
    typeof detailedObject === "object" &&
    !Array.isArray(detailedObject)
  ) {
    assignIfPresent(
      "primary",
      detailedObject.primary_position || detailedObject.primaryPosition
    );
    assignIfPresent(
      "secondary",
      detailedObject.secondary_position || detailedObject.secondaryPosition
    );
    assignIfPresent(
      "tertiary",
      detailedObject.teritory_position ||
        detailedObject.tertiary_position ||
        detailedObject.tertiaryPosition
    );
  }

  const source = Array.isArray(player?.detailed_positions)
    ? player.detailed_positions
    : Array.isArray(player?.detailedPositions)
    ? player.detailedPositions
    : Array.isArray(player?.detailed_position)
    ? player.detailed_position
    : [];

  if (!Array.isArray(source)) {
    return extracted;
  }

  source.forEach((item, index) => {
    if (!item) return;

    let slot = null;
    let value = "";

    if (typeof item === "string") {
      value = item;
      slot = index === 0 ? "primary" : index === 1 ? "secondary" : "tertiary";
    } else if (typeof item === "object") {
      value =
        item.position_code || item.code || item.value || item.position || "";

      const rawSlot = (
        item.position_type ||
        item.type ||
        item.slot ||
        item.order ||
        ""
      )
        .toString()
        .toLowerCase();

      if (["primary", "secondary", "tertiary"].includes(rawSlot)) {
        slot = rawSlot;
      } else {
        slot = index === 0 ? "primary" : index === 1 ? "secondary" : "tertiary";
      }
    }

    if (slot && value) {
      assignIfPresent(slot, value);
    }
  });

  return extracted;
};

const DetailedPositionsModal = ({
  isOpen,
  mode,
  initialValues,
  onClose,
  onSave,
  isSaving = false,
  isFromClub=false
}) => {
  const [values, setValues] = useState(initialValues);

  useEffect(() => {
    if (isOpen) {
      setValues(initialValues);
    }
  }, [initialValues, isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleSave = async () => {
    if (isSaving) return;
    await onSave(values);
  };

  const modalTitle =
    mode === "edit" ? "Edit detailed positions" : "Add detailed positions";

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 px-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold text-[#1A212F]">
              {modalTitle}
            </h2>
            <p className="mt-1 text-sm text-[#1A212F99]">
              Choose up to three detailed positions to associate with this
              player.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-1 text-[#6B7280] transition hover:bg-gray-100"
            aria-label="Close detailed positions modal"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M15 5L5 15"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M5 5L15 15"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>

        <div className="mt-6 space-y-4">
          <CustomDropdown
            label="Primary position"
            value={values.primary}
            onChange={(nextValue) =>
              setValues((prev) => ({ ...prev, primary: nextValue }))
            }
            options={DETAILED_POSITION_OPTIONS}
            placeholder="Select primary position"
            searchEnabled
          />
          <CustomDropdown
            label="Secondary position"
            value={values.secondary}
            onChange={(nextValue) =>
              setValues((prev) => ({ ...prev, secondary: nextValue }))
            }
            options={DETAILED_POSITION_OPTIONS}
            placeholder="Select secondary position"
            searchEnabled
          />
          <CustomDropdown
            label="Tertiary position"
            value={values.tertiary}
            onChange={(nextValue) =>
              setValues((prev) => ({ ...prev, tertiary: nextValue }))
            }
            options={DETAILED_POSITION_OPTIONS}
            placeholder="Select tertiary position"
            searchEnabled
          />
        </div>

        <div className="mt-8 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-[#E5E7EB] px-4 py-2 text-sm font-medium text-[#1F2937] transition hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            className={`rounded-lg px-4 py-2 text-sm font-semibold text-white transition ${
              isSaving
                ? "bg-[#2283FF]/60 cursor-not-allowed"
                : "bg-[#2283FF] hover:bg-blue-600"
            }`}
            disabled={isSaving}
          >
            {isSaving ? "Saving..." : "Save positions"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

const DetailedPositionsControl = ({
  player,
  className = "",
  menuButtonClassName = "",
  popoverClassName = "",
  onSave,
  onContractExpirySave,
  // Photo review flow: when provided, the menu gains a "Change profile photo"
  // entry that opens the shared crop modal (ImageUploadModal) and hands the
  // cropped image back as ({ player, file }). The surface owns the service
  // call and the feedback toasts, mirroring onContractExpirySave.
  onPhotoUpload,
  onRemoveFromSquad,
  disabled = false,
  isFromClub = false,
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isContractModalOpen, setIsContractModalOpen] = useState(false);
  const [isPhotoModalOpen, setIsPhotoModalOpen] = useState(false);
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [modalMode, setModalMode] = useState("add");
  const [initialModalValues, setInitialModalValues] = useState(
    DEFAULT_DETAILED_POSITIONS
  );
  const [savedDetailedPositions, setSavedDetailedPositions] = useState(() =>
    extractDetailedPositionsFromPlayer(player)
  );
  const [isSaving, setIsSaving] = useState(false);

  const menuButtonRef = useRef(null);
  const menuRef = useRef(null);
  const playerIdentifierRef = useRef(getPlayerIdentifier(player));

  const parsedDetailedPositions = useMemo(
    () => extractDetailedPositionsFromPlayer(player),
    [player]
  );

  const hasExistingPositions = useMemo(
    () =>
      !arePositionsEqual(savedDetailedPositions, DEFAULT_DETAILED_POSITIONS),
    [savedDetailedPositions]
  );

  // Check if player has existing contract expiry
  const getContractExpiryDate = () => {
    console.log("Checking contract expiry for player:", player);
    const contractExpiry =
      player?.contract_expires_on || player?.contractExpiresOn;

    if (!contractExpiry || contractExpiry === "N/A" || contractExpiry === "-") {
      return null;
    }

    if (contractExpiry instanceof Date) {
      return contractExpiry;
    }

    const parsed = new Date(contractExpiry);
    if (!isNaN(parsed.getTime())) {
      return parsed;
    }

    return null;
  };

  const contractDate = getContractExpiryDate();
  const hasExistingContract = contractDate !== null;

  useEffect(() => {
    const nextIdentifier = getPlayerIdentifier(player);
    if (playerIdentifierRef.current !== nextIdentifier) {
      playerIdentifierRef.current = nextIdentifier;
      setSavedDetailedPositions(parsedDetailedPositions);
      return;
    }

    if (!arePositionsEqual(parsedDetailedPositions, savedDetailedPositions)) {
      setSavedDetailedPositions(parsedDetailedPositions);
    }
  }, [parsedDetailedPositions, player, savedDetailedPositions]);

  useEffect(() => {
    if (!isMenuOpen) return;

    const handleOutsideClick = (event) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target) &&
        menuButtonRef.current &&
        !menuButtonRef.current.contains(event.target)
      ) {
        setIsMenuOpen(false);
      }
    };

    const handleEscapeDismiss = (event) => {
      if (event.key === "Escape") {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("keydown", handleEscapeDismiss);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("keydown", handleEscapeDismiss);
    };
  }, [isMenuOpen]);

  const toggleMenu = (event) => {
    event.stopPropagation();
    setIsMenuOpen((prev) => !prev);
  };

  const closeMenu = () => setIsMenuOpen(false);

  const handleOpenAddDetailedPositions = (event) => {
    event.stopPropagation();
    setModalMode("add");
    setInitialModalValues(DEFAULT_DETAILED_POSITIONS);
    closeMenu();
    setIsModalOpen(true);
  };

  const handleOpenEditDetailedPositions = (event) => {
    event.stopPropagation();
    setModalMode("edit");
    setInitialModalValues(savedDetailedPositions);
    closeMenu();
    setIsModalOpen(true);
  };

  const handleOpenAddContractExpiry = (event) => {
    event.stopPropagation();
    closeMenu();
    setIsContractModalOpen(true);
  };

  const handleOpenEditContractExpiry = (event) => {
    event.stopPropagation();
    closeMenu();
    setIsContractModalOpen(true);
  };

  const handleOpenRemoveModal = (event) => {
    event.stopPropagation();
    closeMenu();
    setIsRemoveModalOpen(true);
  };

  const handleOpenPhotoModal = (event) => {
    event.stopPropagation();
    closeMenu();
    setIsPhotoModalOpen(true);
  };

  // ImageUploadModal hands back a blob object URL of the cropped image; the
  // API wants a real file, so convert first (same adapter the club logo
  // upload uses in AccountInformation.js).
  const handlePhotoCropped = async (croppedImageUrl) => {
    setIsPhotoModalOpen(false);
    try {
      const response = await fetch(croppedImageUrl);
      const blob = await response.blob();
      const file = new File([blob], "profile_photo.jpg", {
        type: "image/jpeg",
      });
      if (onPhotoUpload) {
        await onPhotoUpload({ player, file });
      }
    } catch (error) {
      // The surface handler owns user-facing feedback (toast) and rethrows;
      // nothing further to do here beyond the trace.
      console.error("Failed to submit profile photo:", error);
    } finally {
      URL.revokeObjectURL(croppedImageUrl);
    }
  };

  const handleConfirmRemove = async (reason) => {
    if (isRemoving) return;
    
    setIsRemoving(true);
    
    try {
      console.log("Removing player from squad:", player, "Reason:", reason);
      
      // Call the parent callback if provided
      if (onRemoveFromSquad) {
        await onRemoveFromSquad(player, reason);
      }
      
      setIsRemoveModalOpen(false);
    } catch (error) {
      console.error("Error removing player from squad:", error);
      // Modal will stay open on error
    } finally {
      setIsRemoving(false);
    }
  };

  const handleSaveDetailedPositions = async (nextValues) => {
    const sanitized = {
      primary: normalizePositionValue(nextValues.primary),
      secondary: normalizePositionValue(nextValues.secondary),
      tertiary: normalizePositionValue(nextValues.tertiary),
    };

    if (arePositionsEqual(sanitized, savedDetailedPositions)) {
      setIsModalOpen(false);
      return;
    }

    const formattedPositions = buildFormattedPositions(sanitized);

    if (!onSave) {
      setSavedDetailedPositions(sanitized);
      setIsModalOpen(false);
      return;
    }

    setIsSaving(true);
    try {
      await onSave({ values: sanitized, formattedPositions, player });
      setSavedDetailedPositions(sanitized);
      setIsModalOpen(false);
    } catch (error) {
      console.error("Failed to save detailed positions:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveContractExpiry = async (selectedDate) => {
    setIsSaving(true);

    try {
      const isoDate =
        selectedDate instanceof Date
          ? selectedDate.toISOString()
          : new Date(selectedDate).toISOString();

      console.log("Saving contract expiry for player:", {
        playerId:
          player?.id || player?.playerAccountId || player?.wyscoutPlayerId,
        playerName: player?.name,
        contractExpiryDate: isoDate,
        rawDate: selectedDate,
      });

      if (onContractExpirySave) {
        await onContractExpirySave({
          player,
          contractExpiryDate: isoDate,
        });
      }

      setIsContractModalOpen(false);
    } catch (error) {
      console.error("Failed to save contract expiry:", error);
    } finally {
      setIsSaving(false);
    }
  };

  if (disabled) {
    return null;
  }

  return (
    <div
      className={`relative ${className}`}
      onClick={(event) => event.stopPropagation()}
    >
      <button
        type="button"
        ref={menuButtonRef}
        onClick={toggleMenu}
        className={`rounded-lg p-1 text-[#6B7280] transition hover:bg-gray-100 hover:text-[#1F2937] ${menuButtonClassName}`}
        aria-haspopup="true"
        aria-expanded={isMenuOpen}
        aria-label="Detailed position actions"
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M10 4.16602C9.07953 4.16602 8.33337 3.41986 8.33337 2.49935C8.33337 1.57883 9.07953 0.83268 10 0.83268C10.9205 0.83268 11.6667 1.57883 11.6667 2.49935C11.6667 3.41986 10.9205 4.16602 10 4.16602Z"
            fill="currentColor"
          />
          <path
            d="M10 11.666C9.07953 11.666 8.33337 10.9199 8.33337 9.99935C8.33337 9.07883 9.07953 8.33268 10 8.33268C10.9205 8.33268 11.6667 9.07883 11.6667 9.99935C11.6667 10.9199 10.9205 11.666 10 11.666Z"
            fill="currentColor"
          />
          <path
            d="M10 19.166C9.07953 19.166 8.33337 18.4199 8.33337 17.4993C8.33337 16.5788 9.07953 15.8327 10 15.8327C10.9205 15.8327 11.6667 16.5788 11.6667 17.4993C11.6667 18.4199 10.9205 19.166 10 19.166Z"
            fill="currentColor"
          />
        </svg>
      </button>

      {isMenuOpen && (
        <div
          ref={menuRef}
          className={`absolute right-0 z-20 mt-2 w-60 rounded-lg border border-[#E5E6E8] bg-white py-2 shadow-lg ${popoverClassName}`}
        >
          {hasExistingPositions ? (
            <button
              type="button"
              onClick={handleOpenEditDetailedPositions}
              className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-[#1A212F] transition hover:bg-gray-50"
            >
              Edit detailed positions
            </button>
          ) : (
            <button
              type="button"
              onClick={handleOpenAddDetailedPositions}
              className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-[#1A212F] transition hover:bg-gray-50"
            >
              Add detailed positions
            </button>
          )}

          {hasExistingContract ? (
            <button
              type="button"
              onClick={handleOpenEditContractExpiry}
              className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-[#1A212F] transition hover:bg-gray-50"
            >
              Edit contract expiry
            </button>
          ) : (
            <button
              type="button"
              onClick={handleOpenAddContractExpiry}
              className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-[#1A212F] transition hover:bg-gray-50"
            >
              Add contract expiry
            </button>
          )}

          {typeof onPhotoUpload === "function" && (
            <button
              type="button"
              onClick={handleOpenPhotoModal}
              className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-[#1A212F] transition hover:bg-gray-50"
            >
              Change profile photo
            </button>
          )}

          {isFromClub && (
            <button
              type="button"
              onClick={handleOpenRemoveModal}
              className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-[#FF4D4F] transition hover:bg-red-50"
            >
              Remove from squad
            </button>
          )}
        </div>
      )}

      <DetailedPositionsModal
        isOpen={isModalOpen}
        mode={modalMode}
        initialValues={initialModalValues}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSaveDetailedPositions}
        isSaving={isSaving}
      />

      <ContractExpiryModal
        isOpen={isContractModalOpen}
        onClose={() => setIsContractModalOpen(false)}
        onSave={handleSaveContractExpiry}
        initialDate={contractDate}
        playerName={player?.name || ""}
        isSaving={isSaving}
      />

      {isPhotoModalOpen && (
        <ImageUploadModal
          documentType="profile_photo"
          onClose={() => setIsPhotoModalOpen(false)}
          onUpload={handlePhotoCropped}
        />
      )}

      <RemovePlayerModal
        isOpen={isRemoveModalOpen}
        onClose={() => setIsRemoveModalOpen(false)}
        onConfirm={handleConfirmRemove}
        playerName={player?.name || "this player"}
        loading={isRemoving}
      />
    </div>
  );
};

export default DetailedPositionsControl;
